import React from 'react';
import { unstable_batchedUpdates as batchedUpdates } from 'react-dom';
import { batchedSubscribe } from 'redux-batched-subscribe';
import { createStore, combineReducers, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import { routerMiddleware, routerReducer, syncHistoryWithStore } from 'react-router-redux';
import { createBrowserHistory } from 'history';
import { connectReducer, connectMiddleware, computeExternalPayload } from 'tpt-connect';

import { currentUserResourceDefinition } from 'dpl/util/tptConnectResourceDefinitions';
import customFetch from 'dpl/util/customFetch';
import { parseQueryString } from 'dpl/util/queryString';
import queryParamsReducer from 'dpl/reducers/query_params';
import mobileMenuReducer from 'dpl/reducers/mobile_menu';
import breederProfileBreedReducer from 'dpl/reducers/breeder_profile_breed';
import toastNotificationsReducer from 'dpl/reducers/toast_notifications';
import infiniteScrollReducer from 'dpl/reducers/infinite_scroll';
import { pushToastNotification } from 'dpl/actions/toast_notifications';
import { TOAST_NOTIFICATIONS_TYPES } from 'dpl/constants/shared';
import ErrorToastMessage from 'dpl/components/ErrorToastMessage';

const RESCUABLE_STATUS_CODES = [
  // not found; let user go back
  404,
  // forbidden; most likely stale csrf token. refresh should fix
  403,
  // we eff'd up; let user refresh and hope for the best
  500,
  // likely nginx error (often deploy/mem related)
  502,
  504
];

if (window.__DPL_CURRENT_USER_ID) {
  // useful only when user IS supposed to be logged in but their session has
  // expired. this will show error and let them reload the page which should
  // then redirect to login.
  //
  // we don't want to do this when user is NOT logged in bc 401s in that case
  // could mean failed login and should be already handled by the login
  // component
  RESCUABLE_STATUS_CODES.push(401);
}

export default function createReduxStore(initialState) {
  // TODO: this is not the same history obj used by react-router
  const history = createBrowserHistory();

  initialState = {
    queryParams: parseQueryString(),
    ...initialState || window.__DPL_INITIAL_STATE || {}
  };

  if (window.__TPT_CONNECT_CURRENT_USER_DATA) {
    initialState.connect = computeExternalPayload(
      currentUserResourceDefinition(),
      window.__TPT_CONNECT_CURRENT_USER_DATA
    );
  }

  const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

  let reduxStore = null;

  /**
   * halt promise resolution so react doesn't barf and user can choose to
   * refresh page on their own
   */
  function toastBoundFetch(...args) {
    return customFetch(...args).then((response) => {
      if (RESCUABLE_STATUS_CODES.includes(response.status)) {
        reduxStore.dispatch(pushToastNotification({
          type: TOAST_NOTIFICATIONS_TYPES.ERROR,
          message: <ErrorToastMessage responseCode={ response.status } />
        }));

        // TODO: this leaks; can probs listen to history changes and resolve
        // this promise if the user decides to continue their session and just
        // navigate away
        return new Promise();
      }

      return response;
    });
  }

  const reduxMiddleware = composeEnhancers(
    applyMiddleware(
      routerMiddleware(history),
      connectMiddleware({ fetch: toastBoundFetch }),
      thunk
    ),
    // for some reason batchedUpdates is not a func in NODE_ENV=test
    batchedSubscribe(batchedUpdates || (notify => notify()))
  );

  reduxStore = createStore(combineReducers({
    connect: connectReducer,
    routing: routerReducer,
    queryParams: queryParamsReducer,
    mobileMenu: mobileMenuReducer,
    breederProfileBreed: breederProfileBreedReducer,
    toastNotifications: toastNotificationsReducer,
    infiniteScroll: infiniteScrollReducer,
    // used to communicate btwn server and redux state
    server: (state = {}) => state
  }), initialState, reduxMiddleware);

  syncHistoryWithStore(history, reduxStore);

  return reduxStore;
}
